"use client";

import { useState } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { FestivalCard } from "@/app/_components/festival-weather/FestivalCard";
import type {
  FestivalConfig,
  FestivalState,
  CompositeScore,
  SafetySeverity,
} from "@/lib/festival-weather/types";

type IndexCard = {
  slug: string;
  name: string;
  city: string;
  country: string;
  region: FestivalConfig["region"];
  indicator: FestivalConfig["indicator"];
  state: FestivalState;
  year: number;
  eventStart: string;
  eventEnd: string;
  confirmedDates: boolean;
  happeningNow: boolean;
  scoreTotal: number | null;
  scoreLabel: string | null;
  primaryRisk: FestivalConfig["primaryRisk"];
  packListOneLiner: string;
  notableStatChip: string | null;
  safetyOverall: Exclude<SafetySeverity, "green"> | null;
  safetyLabels: string[];
};

const REGIONS: { key: "all" | FestivalConfig["region"]; label: string }[] = [
  { key: "all", label: "All" },
  { key: "uk", label: "UK" },
  { key: "europe", label: "Europe" },
  { key: "north-america", label: "North America" },
  { key: "anz", label: "Australia & NZ" },
];

function reconstructScore(c: IndexCard): CompositeScore | null {
  if (c.scoreTotal === null || c.scoreLabel === null) return null;
  return {
    total: c.scoreTotal,
    label: c.scoreLabel,
    primaryAxis: c.primaryRisk,
    axisValue: 0,
    factors: [],
  };
}

export function FestivalIndexContent({ cards }: { cards: IndexCard[] }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const regionParam = searchParams.get("region") ?? "all";
  const [region, setRegion] = useState<string>(regionParam);

  const apply = (next: string) => {
    setRegion(next);
    const params = new URLSearchParams(searchParams.toString());
    if (next === "all") params.delete("region");
    else params.set("region", next);
    router.replace(
      `${pathname}${params.toString() ? `?${params.toString()}` : ""}`,
      { scroll: false }
    );
  };

  const filtered =
    region === "all"
      ? cards
      : cards.filter((c) => c.region === region);

  const live = filtered.filter((c) => c.state === "live_forecast");
  const coming = filtered.filter((c) => c.state === "climate");
  const finished = filtered.filter(
    (c) =>
      c.state === "recently_finished" ||
      c.state === "awaiting_snapshot" ||
      c.state === "awaiting_archive"
  );

  live.sort((a, b) => a.eventStart.localeCompare(b.eventStart));
  coming.sort((a, b) => a.eventStart.localeCompare(b.eventStart));
  finished.sort((a, b) => b.eventEnd.localeCompare(a.eventEnd));

  return (
    <section className="max-w-[1000px] mx-auto px-5 md:px-12 py-10 md:py-14">
      {/* Sticky region filter */}
      <div className="sticky top-0 z-10 bg-cream -mx-5 md:-mx-12 px-5 md:px-12 py-4 mb-8 md:mb-10 flex gap-2 overflow-x-auto">
        {REGIONS.map((r) => {
          const isActive = region === r.key;
          return (
            <button
              key={r.key}
              onClick={() => apply(r.key)}
              className={`px-4 py-2 rounded-full text-[13px] font-semibold whitespace-nowrap border transition-colors ${
                isActive
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-navy border-border-light hover:border-gold/40"
              }`}
            >
              {r.label}
            </button>
          );
        })}
      </div>

      {live.length > 0 && <Group title="Live forecast available" cards={live} />}
      {coming.length > 0 && <Group title="Coming up" cards={coming} />}
      {finished.length > 0 && (
        <CollapsibleGroup title="Recently finished" cards={finished} />
      )}
      {filtered.length === 0 && (
        <div className="text-center text-text-muted py-16">
          No festivals in this region.
        </div>
      )}
    </section>
  );
}

function Group({ title, cards }: { title: string; cards: IndexCard[] }) {
  return (
    <div className="mb-12 md:mb-16">
      {title && (
        <h2 className="font-serif text-[24px] md:text-[30px] font-bold text-navy mb-5 md:mb-6 tracking-[-0.01em]">
          {title}
          <span className="text-[13px] text-text-muted font-normal ml-3">
            {cards.length}
          </span>
        </h2>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {cards.map((c) => (
          <FestivalCard
            key={c.slug}
            festival={{
              slug: c.slug,
              name: c.name,
              city: c.city,
              country: c.country,
              region: c.region,
              indicator: c.indicator,
              primaryRisk: c.primaryRisk,
            }}
            state={c.state}
            year={c.year}
            eventStart={new Date(c.eventStart)}
            eventEnd={new Date(c.eventEnd)}
            confirmedDates={c.confirmedDates}
            happeningNow={c.happeningNow}
            score={reconstructScore(c)}
            packListOneLiner={c.packListOneLiner}
            notableStatChip={c.notableStatChip}
            safetyOverall={c.safetyOverall}
            safetyLabels={c.safetyLabels}
          />
        ))}
      </div>
    </div>
  );
}

function CollapsibleGroup({
  title,
  cards,
}: {
  title: string;
  cards: IndexCard[];
}) {
  const [open, setOpen] = useState(cards.length <= 3);
  return (
    <div className="mb-12 md:mb-16">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full mb-5 md:mb-6 text-left"
        aria-expanded={open}
      >
        <h2 className="font-serif text-[24px] md:text-[30px] font-bold text-navy tracking-[-0.01em]">
          {title}
          <span className="text-[13px] text-text-muted font-normal ml-3">
            {cards.length}
          </span>
        </h2>
        <span aria-hidden className="text-gold text-2xl leading-none">
          {open ? "−" : "+"}
        </span>
      </button>
      {open && <Group title="" cards={cards} />}
    </div>
  );
}
